import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material"

/**
 * OnboardingBanner
 *
 * Renders the Stripe Connect onboarding status bar at the top of the wallet page.
 * Status flow:  NOT_STARTED → INCOMPLETE → PENDING → COMPLETE
 */
function OnboardingBanner({ connectStatus, connectLoading, connectError, onboardingLoading, onStart }) {
  // Still loading connect status for the first time
  if (connectLoading && !connectStatus) {
    return (
      <Box mb={3}>
        <LinearProgress sx={{ borderRadius: 4 }} />
      </Box>
    )
  }

  // API failed to fetch status — show a non-destructive error (don't block the whole page)
  if (!connectLoading && connectError && !connectStatus) {
    return (
      <Box
        mb={3}
        sx={{
          p: "12px 20px",
          borderRadius: "12px",
          background: "linear-gradient(135deg,#fff5f5,#fecaca)",
          border: "1px solid #fca5a5",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Typography fontSize="13px" color="#b91c1c" fontWeight={500}>
          ⚠ Could not load your Stripe account status. Please refresh the page.
        </Typography>
      </Box>
    )
  }

  // Fully onboarded — show a minimal success badge
  if (connectStatus?.onboardingComplete) {
    return (
      <Box
        sx={{
          mb: 3,
          p: "12px 20px",
          borderRadius: "12px",
          background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
          border: "1px solid #6ee7b7",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: "#22c55e",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "16px",
            flexShrink: 0,
          }}
        >
          ✓
        </Box>
        <Box>
          <Typography fontWeight={700} fontSize="14px" color="#065f46">
            Payouts Enabled
          </Typography>
          <Typography fontSize="12px" color="#047857">
            Your Stripe account is fully verified. You can receive payouts.
          </Typography>
        </Box>
      </Box>
    )
  }

  // Not connected at all
  if (!connectStatus?.connected) {
    return (
      <OnboardingCard
        step={1}
        totalSteps={3}
        title="Start Payout Onboarding"
        description="Connect your Stripe account to receive earnings from your sessions.
          This takes about 5 minutes — Stripe handles all KYC and bank verification."
        ctaLabel="Start Onboarding"
        ctaColor="#4f46e5"
        loading={onboardingLoading}
        onAction={onStart}
        severity="info"
      />
    )
  }

  // Account disabled by Stripe
  if (connectStatus?.accountDisabled) {
    return (
      <OnboardingCard
        step={3}
        totalSteps={3}
        title="Account Disabled"
        description="Stripe has restricted operations on your account. Please check your Stripe dashboard or email for more details."
        ctaLabel="Go to Stripe Dashboard"
        ctaColor="#ef4444"
        loading={onboardingLoading}
        onAction={onStart}
        severity="error"
      />
    )
  }

  // Connected but incomplete (Action Required)
  if (connectStatus?.actionRequired) {
    return (
      <OnboardingCard
        step={2}
        totalSteps={3}
        title="Action Required"
        description="We need more information to verify your account and enable payouts. Please complete your identity and business details."
        ctaLabel="Complete Verification"
        ctaColor="#d97706"
        loading={onboardingLoading}
        onAction={onStart}
        severity="warning"
      />
    )
  }

  // Verification Pending (No action required, but not fully onboarded)
  if (connectStatus?.verificationPending) {
    return (
      <OnboardingCard
        step={3}
        totalSteps={3}
        title="Verification Pending"
        description="Your details are under review by Stripe. This usually takes 1-2 business days. Payouts will be enabled once approved."
        ctaLabel="Check Again"
        ctaColor="#0891b2"
        loading={onboardingLoading}
        onAction={onStart}
        severity="info"
        noAction
      />
    )
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable card layout
// ─────────────────────────────────────────────────────────────────────────────
const STEP_COLORS = {
  info: { bg: "linear-gradient(135deg,#eff6ff,#dbeafe)", border: "#93c5fd", text: "#1e40af" },
  warning: {
    bg: "linear-gradient(135deg,#fffbeb,#fef3c7)",
    border: "#fcd34d",
    text: "#92400e",
  },
  error: {
    bg: "linear-gradient(135deg,#fef2f2,#fee2e2)",
    border: "#fca5a5",
    text: "#991b1b",
  },
}

function OnboardingCard({
  step,
  totalSteps,
  title,
  description,
  ctaLabel,
  ctaColor,
  loading,
  onAction,
  severity,
  noAction = false,
}) {
  const palette = STEP_COLORS[severity] || STEP_COLORS.info
  const progress = Math.round((step / totalSteps) * 100)

  return (
    <Box
      sx={{
        mb: 3,
        p: 3,
        borderRadius: "16px",
        background: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      {/* Step indicator */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {Array.from({ length: totalSteps }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: i < step ? 28 : 22,
                height: 8,
                borderRadius: 4,
                bgcolor: i < step ? ctaColor : "#d1d5db",
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </Box>
        <Typography fontSize="12px" fontWeight={600} color={palette.text}>
          Step {step} of {totalSteps}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mb: 2,
          height: 6,
          borderRadius: 4,
          bgcolor: "#e5e7eb",
          "& .MuiLinearProgress-bar": { bgcolor: ctaColor, borderRadius: 4 },
        }}
      />

      <Typography fontWeight={700} fontSize="15px" color={palette.text} mb={0.5}>
        {title}
      </Typography>
      <Typography fontSize="13px" color={palette.text} mb={2} sx={{ opacity: 0.85 }}>
        {description}
      </Typography>

      {!noAction && (
        <Button
          variant="contained"
          size="small"
          disabled={loading}
          onClick={onAction}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            bgcolor: ctaColor,
            "&:hover": { bgcolor: ctaColor, filter: "brightness(0.92)" },
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            px: 2.5,
            py: 1,
            fontSize: "13px",
            boxShadow: "none",
          }}
        >
          {loading ? "Redirecting…" : ctaLabel}
        </Button>
      )}

      {noAction && (
        <Alert severity="info" sx={{ mt: 0, fontSize: "12px", borderRadius: "8px" }}>
          Stripe usually approves accounts within 1–2 business days. No action required.
        </Alert>
      )}
    </Box>
  )
}

export default OnboardingBanner
